<template>
  <card-layout
    :id="cardId"
    class="card"
    :class="[cardClasses]"
    :full-height="fullHeight"
    :above-search="aboveSearch"
    :expanded="expanded"
    data-test="card"
  >
    <header>
      <div class="card-masthead">
        <div v-if="showBackLink" class="card-back-link">
          <button data-test="card-back-link" @click="handleBackClick">
            <x-icon :icon="backLinkIcon" height="20" />
            <span v-if="backLinkLabel" class="back-link-label">
              {{ backLinkLabel }}
            </span>
          </button>
        </div>
        <div v-if="showCollapseControl" class="card-collapse">
          <x-button
            :appearance-color="
              collapsedView ? 'secondary-light' : 'secondary-outline-light'
            "
            appearance-display="inline"
            appearance-size="small"
            :disabled="disableCollapse"
            @click="handleCollapseCard(!collapsedView)"
          >
            <x-icon
              :icon="collapsedView ? 'expand' : 'collapse'"
              class="icon"
              width="15"
              height="15"
            />
          </x-button>
        </div>
        <div v-if="!showCollapseControl" class="close">
          <button
            title="Close card"
            class="card-close"
            data-test="Card-Close"
            @click="handleCloseClick"
          >
            <x-icon
              icon="close"
              width="25"
              height="25"
              :color="closeIconColor"
            />
          </button>
        </div>
      </div>
      <div class="card-title-header">
        <div
          v-if="hasAnyTitles"
          class="titles"
          :class="{ 'collapse-title': showCollapseControl }"
          data-test="card-title"
        >
          <x-metadata-text
            v-if="secondaryTitle"
            weight="medium"
            class="secondary-title"
            data-test="card-secondary-title"
          >
            <x-icon
              v-if="secondaryTitleIcon"
              :icon="secondaryTitleIcon"
              class="secondary-title-icon"
            />
            {{ secondaryTitle }}
          </x-metadata-text>
          <x-heading
            v-if="title"
            :size="headerBackgroundColor ? '5' : '3'"
            :class="['title', { 'with-secondary-title': secondaryTitle }]"
          >
            {{ title }}
            <button
              v-if="showHelp"
              class="open-help"
              @click="handleHelp(collapsedView)"
            >
              <x-icon icon="question-mark" title="Help" />
            </button>
          </x-heading>
          <slot name="subTitle"></slot>
        </div>
        <div
          v-if="$slots['inline-header-option']"
          class="inline-header-options"
        >
          <slot name="inline-header-option" />
        </div>
      </div>
      <div class="options">
        <slot name="header-option" />
      </div>
    </header>
    <div
      v-if="tabs && !collapsedView"
      class="tabs-container"
      data-test="tabs-container"
    >
      <button
        v-if="tabsOverflow"
        ref="arrowLeft"
        class="arrow arrow-left hidden"
        @click="scrollLeft"
      >
        <x-icon icon="chevron-left" color="#222" width="10" />
      </button>
      <div ref="tabsContainer" class="tabs-inner-container">
        <ul
          ref="tabsList"
          class="tabs"
          :class="tabsOverflow ? 'hide-left-shade' : 'hide-all-shades'"
        >
          <li v-for="(tab, index) in tabs" :key="index">
            <!-- Router tabs -->
            <router-link
              v-if="tab.routeName"
              class="tab"
              :to="{
                name: tab.routeName,
                params: $route.params,
                query: $route.query,
              }"
              replace
              :data-test="tab.id"
              @click="sendTabEvent(tab.label, tab.id)"
            >
              {{ tab.label }}
            </router-link>
            <!-- Tabs unassociated with a route-->
            <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
            <div
              v-else
              :data-test="tab.id"
              :class="['tab', { active: tab.active }]"
              @click="sendTabEvent(tab.label, tab.id)"
            >
              {{ tab.label }}
            </div>
          </li>
        </ul>
      </div>
      <button
        v-if="tabsOverflow"
        ref="arrowRight"
        class="arrow arrow-right"
        @click="scrollRight"
      >
        <x-icon icon="chevron-right" color="#222" width="10" />
      </button>
    </div>
    <div v-show="loading" class="loading-spinner" />
    <slot v-if="!collapsedView" />
    <footer v-if="$slots.footer && !collapsedView">
      <slot name="footer" />
    </footer>
    <div v-if="$slots.toolbar" class="toolbar">
      <slot name="toolbar" />
    </div>
    <div v-if="$slots.modals">
      <slot name="modals" />
    </div>
  </card-layout>
</template>

<!-- eslint-disable vue/block-lang -->
<script>
/* eslint-disable vue/component-api-style */
import cardLayout from '@/components/shared/card-layout'
import xButton from '@/components/shared/x-button'
import xHeading from '@/components/shared/x-heading'
import xMetadataText from '@/components/shared/x-metadata-text'
import xIcon from '@/components/shared/x-icon'
import colors from '@/styles/exports/_colors-export.scss'

import MarketingEventService from '@/services/marketing-event-service'
import { getThemeClasses } from '@/lib/theme-utils'
import pubsub from '@/lib/pubsub'
import { EVENTS } from '@/lib/enums-marketing'

export default {
  components: {
    cardLayout,
    xHeading,
    xMetadataText,
    xIcon,
    xButton,
  },
  inject: {
    theme: { default: 'hunt' },
  },
  props: {
    cardId: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    secondaryTitle: {
      type: String,
      required: false,
      default: '',
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    tabs: {
      type: Array,
      required: false,
      default: null,
    },
    closeIconColor: {
      type: String,
      required: false,
      default: colors.Grey100,
    },
    aboveSearch: {
      type: Boolean,
      default: false,
    },
    fullHeight: {
      type: Boolean,
      default: false,
    },
    showCollapseControl: {
      type: Boolean,
      default: false,
    },
    disableCollapse: {
      type: Boolean,
      default: false,
    },
    showBackLink: {
      type: Boolean,
      default: false,
    },
    showHelp: {
      type: Boolean,
      default: false,
    },
    backLinkLabel: {
      type: String,
      default: '',
    },
    backLinkIcon: {
      type: String,
      default: 'chevron-left',
    },
    returnToMapOnClose: {
      type: Boolean,
      default: false,
    },
    zeroHeaderHeight: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    headerBackgroundColor: {
      type: Boolean,
      default: false,
    },
    secondaryTitleIcon: {
      type: String,
      default: undefined,
    },
  },
  emits: [
    'tab-changed',
    'go-back',
    'close-card',
    'collapsed-card',
    'open-help',
  ],
  data() {
    return {
      tabsOverflow: false,
      collapsedView: false,
    }
  },
  computed: {
    cardClasses() {
      return {
        ...getThemeClasses(this.theme),
        aboveSearch: this.aboveSearch,
        hasBackLink: this.showBackLink,
        zeroHeaderHeight: this.zeroHeaderHeight,
        collapsedView: this.collapsedView,
        headerBackgroundColor: this.headerBackgroundColor,
      }
    },
    hasAnyTitles() {
      return this.secondaryTitle || this.title
    },
    activeTab() {
      return (
        this.tabs?.find(
          (tab) =>
            this.$router.currentRoute.value.name === tab.routeName || tab.active
        ) ?? null
      )
    },
  },
  watch: {
    activeTab: {
      handler(tab) {
        if (tab) {
          this.openTab(tab)
        }
      },
      flush: 'post',
    },
    tabs() {
      this.setTabOverflow()
    },
  },
  created() {
    this.tabs_ = this.tabs
  },
  mounted() {
    this.setTabOverflow()
    if (this.activeTab) {
      this.openTab(this.activeTab)
      MarketingEventService.appendToStringList(
        EVENTS.QUERIED_MAP,
        'tabs_viewed',
        this.activeTab.label
      )
    }

    pubsub.subscribe('collapse-card', () => {
      this.handleCollapseCard(true)
    })

    // Reset tab overflow on window resize
    window.addEventListener('resize', this.setTabOverflow)
  },
  unmounted() {
    window.removeEventListener('resize', this.setTabOverflow)
  },

  methods: {
    openTab(tab) {
      const newIndex = this.tabs.indexOf(tab)

      // Scroll tab into view
      const tabElement = this.$el.querySelectorAll('.tab')[newIndex]
      tabElement.scrollIntoView({ inline: 'center' })
      const tabsElement = this.$refs.tabsList
      const leftArrow = this.$refs.arrowLeft
      const rightArrow = this.$refs.arrowRight

      // Make sure arrows and shading are visible
      leftArrow?.classList?.remove('hidden')
      rightArrow?.classList?.remove('hidden')

      if (leftArrow && rightArrow) {
        tabsElement.classList.remove('hide-right-shade')
        tabsElement.classList.remove('hide-left-shade')
      }

      // Hide the left arrow if the furthest left tab is opened
      if (newIndex === 0) {
        leftArrow?.classList.add('hidden')
        tabsElement.classList.add('hide-left-shade')
      }

      // Hide the right arrow if the furthest right tab is opened
      else if (newIndex === this.tabs.length - 1) {
        rightArrow?.classList.add('hidden')
        tabsElement.classList.add('hide-right-shade')
      }
    },
    async setTabOverflow() {
      if (this.tabs) {
        await this.$nextTick()
        const tabContainerWidth = this.$refs.tabsContainer?.clientWidth
        const tabListWidth = this.$refs.tabsList?.clientWidth
        this.tabsOverflow = tabListWidth > tabContainerWidth
      }
    },
    scrollLeft() {
      const nextTab = this.tabs[this.tabs.indexOf(this.activeTab) - 1]
      if (nextTab) {
        this.changeTab(nextTab)
      }
    },
    scrollRight() {
      const nextTab = this.tabs[this.tabs.indexOf(this.activeTab) + 1]
      if (nextTab) {
        this.changeTab(nextTab)
      }
    },
    changeTab(tab) {
      if (tab.routeName) {
        this.$router
          .replace({
            name: tab.routeName,
            params: this.$router.params,
            query: this.$router.query,
          })
          .catch(() => {})
      }
      this.sendTabEvent(tab.label, tab.id)
    },
    handleBackClick() {
      this.$emit('go-back')
    },
    handleCollapseCard(isCollapsed) {
      this.collapsedView = isCollapsed
      this.$emit('collapsed-card', isCollapsed)
    },
    handleHelp(isCollapsed) {
      this.$emit('open-help', isCollapsed)
      if (isCollapsed) {
        this.handleCollapseCard(!isCollapsed)
      }
    },
    handleCloseClick() {
      this.$emit('close-card')

      if (this.returnToMapOnClose) {
        this.$router.push({ name: `fish-map` }).catch(() => {})
      }
    },
    sendTabEvent(tabLabel, id) {
      MarketingEventService.appendToStringList(
        EVENTS.QUERIED_MAP,
        'tabs_viewed',
        tabLabel
      )
      this.$emit('tab-changed', {
        tabName: tabLabel,
        id: id,
      })
    },
  },
}
</script>
<style lang="scss" scoped>
@use 'sass:map';
@use '@/styles/system/_breakpoints.scss';
@use '@/styles/system/_colors.scss';
@use '@/styles/system/_shadow.scss';
@use '@/styles/system/_type.scss';
@use '@/styles/system/_themes.scss';
@use '@/styles/system/_layout.scss';

$card-masthead-height: 50px;

.side-panel.panel-view.card,
.card {
  &.hidden {
    display: none;
  }
  &.hasBackLink {
    header {
      padding-top: calc(#{$card-masthead-height} + 0.5rem);
    }

    .inline-header-options {
      margin-top: 8px;
    }

    .card-masthead {
      justify-content: space-between;
    }
  }
  &:not(.hasBackLink) {
    .inline-header-options {
      margin-top: calc(#{$card-masthead-height} - 0.25rem);
    }
  }
  &.zeroHeaderHeight {
    header {
      min-height: 0;
      height: 0;
    }
  }

  &.headerBackgroundColor {
    header {
      background-color: var(--ys-color-background-secondary);
      padding-top: 14px;

      .card-title-header .titles.collapse-title {
        position: relative;
        top: 0;
      }
    }

    &.hasBackLink {
      header {
        padding-left: 50px;
      }
    }
  }
  &.collapsedView {
    height: auto;
    overflow-y: hidden;
    header {
      min-height: 45px;
    }
    .toolbar {
      left: 120px;
    }
  }
  &.basic-user {
    .toolbar {
      top: 123px;
    }
  }
  header {
    flex: 0 0 auto;
    display: flex;
    flex-flow: row wrap;
    align-items: flex-start;
    border-radius: 5px 5px 0 0;
    padding: 20px 50px 0 20px;
    min-height: $card-masthead-height;
    background: transparent;

    .card-masthead {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: $card-masthead-height;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      z-index: 1;

      .card-close {
        height: $card-masthead-height;
        font-size: 0.8rem;
        color: colors.$BaseBlack;
        background: transparent;
        outline: none;
      }

      .card-collapse {
        margin-right: 5px;
        margin-top: -5px;
        .x-button {
          border-width: 2px;
          border-color: colors.$Grey30;
          width: 36px;
          padding-top: 11px;
          &.secondary-light {
            border-color: colors.$Grey40;
          }
        }
      }

      .card-back-link {
        margin-left: 0.5rem;
        font-size: 14px;

        button {
          display: flex;
          align-items: center;
          background: none;
          color: colors.$BaseBlack;
          margin: 0;
          text-wrap: wrap;
          text-align: left;
          .back-link-label {
            @include layout.lineClamp(1);
          }
        }

        .x-icon {
          margin-right: 0.75rem;
        }
      }
    }
    .card-title-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-end;
      min-width: 100%;

      .inline-header-options {
        display: flex;
        justify-content: flex-end;
        height: fit-content;
        min-width: 32px;
        padding-bottom: 8px;
      }

      .titles {
        position: relative;
        flex: 2 1 auto;
        display: flex;
        flex-flow: column nowrap;
        justify-content: flex-end;
        min-width: 100%;
        z-index: 2;

        &.collapse-title {
          position: absolute;
          top: 6px;
          min-width: auto;
        }

        .title {
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 320px;
        }

        .support-icon {
          cursor: pointer;
          vertical-align: middle;
        }
      }
    }

    .options {
      flex: 0 1 auto;
      display: flex;
      flex-flow: column nowrap;
      justify-content: flex-end;
    }
    button.open-help {
      width: 24px;
      padding: 0;
      height: 24px;
      vertical-align: middle;
      background-color: transparent;
      margin-left: 5px;
      margin-top: -4px;
      .x-icon {
        height: 24px;
      }
    }
  }

  :deep(.content) {
    flex: 1 1 auto;
  }

  :deep(.card-actions) {
    padding: 32px 20px;

    @include breakpoints.app-prototyping {
      padding-top: 12px;
      padding-bottom: 12px;
    }

    &.raised {
      box-shadow: 4px 0px 20px transparentize(colors.$TrueBlack, 0.8);
    }

    button {
      margin-bottom: 0;
    }

    &.vertical {
      & > button {
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        width: 100%;
        margin-bottom: 10px;

        &:last-child {
          margin-bottom: 0;
        }
      }

      & > ys-button {
        margin-bottom: var(--ys-spacing-100);

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .tabs-container {
    position: relative;
    margin-top: var(--ys-spacing-250);
    padding: 0 var(--ys-spacing-250);
    border-bottom: 1px solid var(--ys-color-divider-base);

    .tabs-inner-container {
      display: flex;
      flex-direction: row;
      min-height: 37px;
      width: 100%;
      overflow: hidden;
    }

    .tabs {
      width: fit-content;
      display: flex;
      flex-direction: row;
      gap: var(--ys-spacing-200);

      .tab {
        @include type.title5;
        cursor: pointer;
        flex: 0 0 auto;
        padding-bottom: var(--ys-spacing-150);
        color: var(--ys-color-divider-tertiary);
        white-space: nowrap;
      }

      &.hide-all-shades {
        &:before,
        &:after {
          content: none;
        }
      }

      &.hide-left-shade {
        &:before {
          content: none;
        }
      }

      &.hide-right-shade {
        &:after {
          content: none;
        }
      }

      &:before {
        content: '';
        position: absolute;
        z-index: 1;
        left: 0;
        pointer-events: none;
        background-image: linear-gradient(
          to left,
          rgba(255, 255, 255, 0),
          rgba(255, 255, 255, 1) 70%
        );
        width: 30%;
        height: 100%;
      }

      &:after {
        content: '';
        position: absolute;
        z-index: 1;
        right: 0;
        pointer-events: none;
        background-image: linear-gradient(
          to right,
          rgba(255, 255, 255, 0),
          rgba(255, 255, 255, 1) 70%
        );
        width: 30%;
        height: 100%;
      }
    }

    .arrow {
      z-index: 10;
      position: absolute;
      top: 0;
      background-color: transparent;
      color: var(--ys-color-button-text-secondary-active);
      padding: 0.1em 0 0.35em 0;
      outline: none;

      &.hidden {
        display: none;
      }

      &.arrow-left {
        left: 20px;
      }

      &.arrow-right {
        right: 20px;
      }
    }
  }

  @include themes.ApplyTheme using($themeMap) {
    .tab.router-link-active,
    .tab.active {
      color: var(--ys-color-button-text-secondary-active);
      border-bottom: 3px solid map.get($themeMap, 'primary');
    }
  }

  .loading-spinner {
    margin: 20px auto 0;
  }

  :deep(footer) {
    flex: 0 0 auto;
    border-radius: 0px 0px 5px 5px;
  }

  .toolbar {
    position: fixed;
    left: 515px;
    top: 85px;
  }

  &.expanded {
    header {
      padding: 32px 50px 0 32px;
    }

    .tabs-container {
      padding: 0 32px;
    }
  }

  .secondary-title-icon {
    margin-right: var(--ys-spacing-50);
  }
}

::v-slotted(.content) {
  padding: 0 20px 20px;
  overflow-y: visible;
  overflow-x: hidden;
  width: 100%;
}
</style>
